import { InfiniteData, UseMutationOptions, useQueryClient } from 'react-query';
import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';

import {
  FeedKey,
  CreateGroupMutation,
  CreateGroupMutationVariables,
  GetFollowedQuery,
  GroupRole,
  useGetFollowedQuery,
  BootstrapQuery,
  UserFeedFollowFragment,
  GroupModeratorFragment,
  UserProfileFragment,
} from 'api';
import { useNotify, useCurrentUser, useBootstrapQueryKey } from 'hooks';
import { FOLLOW_QUERIES_LIMIT } from 'app-constants';

type Mutation = CreateGroupMutation;
type MutationVariables = CreateGroupMutationVariables;

type FollowedQueryData = InfiniteData<GetFollowedQuery>;
type BootstrapQueryData = BootstrapQuery;

const ID_STUB = uuidv4();

const useCreateGroup = () => {
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const followedQueryKey = useGetFollowedQuery.getKey({
    data: { user_id: currentUser.id, limit: FOLLOW_QUERIES_LIMIT },
  });
  const bootstrapQueryKey = useBootstrapQueryKey();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: async mutationArgs => {
      await queryClient.cancelQueries(followedQueryKey);
      await queryClient.cancelQueries(bootstrapQueryKey);

      const newFeedFollow: UserFeedFollowFragment = {
        group: {
          id: ID_STUB,
          color_map: null,
          icon_map: null,
          ...mutationArgs.data,
        },
        followed_by_user: currentUser,
        user_id: currentUser.id,
        postable: true,
        created_at: Date.now(),
        feed: FeedKey.Group,
        group_role: GroupRole.Owner,
      };

      const newModerator: GroupModeratorFragment = {
        group_id: ID_STUB,
        user_id: currentUser.id,
        created_at: Date.now(),
        added_by_user_id: currentUser.id,
        user: currentUser,
      };

      const baseFollowedState = queryClient.getQueryData<FollowedQueryData>(followedQueryKey);
      const baseBootstrapState = queryClient.getQueryData<BootstrapQueryData>(bootstrapQueryKey);

      const nextFollowedState = produce(baseFollowedState, draftState => {
        draftState.pages[0].getPaginatedUserFeedFollows.feed_follows.splice(0, 0, newFeedFollow);
      });

      const nextBootstrapState = produce(baseBootstrapState, draftState => {
        const { group_moderator } = draftState.bootstrap.user as UserProfileFragment;
        (draftState.bootstrap.user as UserProfileFragment).group_moderator = group_moderator
          ? group_moderator.splice(0, 0, newModerator)
          : [newModerator];
      });

      if (baseFollowedState) {
        queryClient.setQueryData<FollowedQueryData>(followedQueryKey, nextFollowedState);
      }

      if (baseBootstrapState) {
        queryClient.setQueryData<BootstrapQueryData>(bootstrapQueryKey, nextBootstrapState);
      }

      return { baseFollowedState, baseBootstrapState };
    },

    onError: (error, mutationArgs, { baseFollowedState, baseBootstrapState }) => {
      if (baseFollowedState) {
        queryClient.setQueryData<FollowedQueryData>(followedQueryKey, baseFollowedState);
      }

      if (baseBootstrapState) {
        queryClient.setQueryData<BootstrapQueryData>(bootstrapQueryKey, baseBootstrapState);
      }

      notify.mutationError();
    },
    onSettled: ({ createGroup }) => {
      const baseFollowedState = queryClient.getQueryData<FollowedQueryData>(followedQueryKey);
      const baseBootstrapState = queryClient.getQueryData<BootstrapQueryData>(bootstrapQueryKey);

      const nextFollowedState = produce(baseFollowedState, draftState => {
        draftState.pages.forEach(page => {
          const index = page.getPaginatedUserFeedFollows.feed_follows.findIndex(feedFollow => {
            return feedFollow.group?.id === ID_STUB;
          });
          if (index !== -1) {
            page.getPaginatedUserFeedFollows.feed_follows[index].group = createGroup;
          }
        });
      });

      const nextBootstrapState = produce(baseBootstrapState, draftState => {
        (draftState.bootstrap.user as UserProfileFragment).group_moderator.map(moderator => {
          if (moderator.group_id === ID_STUB) {
            moderator.group_id = createGroup.id;
          }
        });
      });

      if (baseFollowedState) {
        queryClient.setQueryData<FollowedQueryData>(followedQueryKey, nextFollowedState);
      }

      if (baseBootstrapState) {
        queryClient.setQueryData<BootstrapQueryData>(bootstrapQueryKey, nextBootstrapState);
      }
    },
  };

  return mutationOptions;
};

export default useCreateGroup;
